"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { claimPlayback } from "./single-playback";

export interface VoiceEngine {
  /** The media element that plays the note, once the visitor has tapped (never before). */
  audio: HTMLAudioElement | null;
  playing: boolean;
  /** Tapped, nothing audible yet (buffering). Cleared by the first sound, a refusal or an error. */
  starting: boolean;
  /** The element could not play the file (404, unsupported codec, network). */
  failed: boolean;
  /** Play or pause. Call it SYNCHRONOUSLY from the tap handler: that is what keeps iOS willing. */
  toggle: () => void;
  stop: () => void;
}

/**
 * The playback engine of a voice note: a plain HTMLMediaElement created and `play()`ed inside the
 * visitor's tap. iOS Safari only starts audio from a user gesture, so playback must not wait for the
 * (async) waveform decode; wavesurfer is attached to this element afterwards (`media` option) and
 * only draws. Nothing is created on mount. A `play()` the browser refuses is logged once and leaves
 * the note paused (not stuck on "playing"); the next tap is a new gesture on the same element.
 */
export function useVoiceEngine(src: string): VoiceEngine {
  const ref = useRef<HTMLAudioElement | null>(null);
  const warned = useRef(false);
  const dispose = useRef<(() => void) | null>(null);
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState(false);

  const create = useCallback(() => {
    const el = new Audio();
    el.preload = "auto";
    el.src = new URL(src, document.baseURI).href;
    const on: [string, () => void][] = [
      ["play", () => setPlaying(true)],
      ["playing", () => setStarting(false)],
      ["pause", () => setPlaying(false)],
      ["ended", () => setPlaying(false)],
      [
        "error",
        () => {
          setStarting(false);
          setPlaying(false);
          setFailed(true);
        },
      ],
    ];
    for (const [name, fn] of on) el.addEventListener(name, fn);
    dispose.current = () => {
      for (const [name, fn] of on) el.removeEventListener(name, fn);
      el.pause();
      el.removeAttribute("src");
      el.load();
    };
    ref.current = el;
    setAudio(el);
    return el;
  }, [src]);

  const toggle = useCallback(() => {
    const el = ref.current ?? create();
    if (!el.paused) {
      el.pause();
      return;
    }
    setStarting(true);
    // Synchronous with the tap. The promise only reports a refusal.
    el.play().catch((error: unknown) => {
      setStarting(false);
      // A pause (or new load) interrupting the request is not a refusal.
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (!warned.current) {
        warned.current = true;
        console.warn(
          "[wexio] the browser refused to start the voice note (it must start from a tap); tap play again",
          error,
        );
      }
    });
  }, [create]);

  const stop = useCallback(() => {
    ref.current?.pause();
    setStarting(false);
  }, []);

  // One note audible at a time: while this one plays it owns the claim.
  useEffect(() => {
    if (!playing) return;
    return claimPlayback(() => ref.current?.pause());
  }, [playing]);

  useEffect(
    () => () => {
      dispose.current?.();
      dispose.current = null;
      ref.current = null;
    },
    [],
  );

  return { audio, playing, starting, failed, toggle, stop };
}
